import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import styled from 'styled-components';
import { colors } from '../theme';
import Message from './Message';

const ChatHistoryContainer = styled(Box)`
  flex-grow: 1;
  background-color: ${colors.background};
  padding: ${props => props.$isMobile ? '12px' : '16px 20px'};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
  
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 1) 1%,
    rgba(0, 0, 0, 1) 99.5%,
    transparent 100%
  );
  
  &::-webkit-scrollbar {
    width: ${props => props.$isMobile ? '4px' : '8px'};
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ce7a74;
    border-radius: 4px;
    
    &:hover {
      background: #b56560;
    }
  }
`;

const WelcomeMessageContainer = styled(Paper)`
  && {
    background: transparent;
    color: ${colors.text.secondary};
    padding: ${props => props.$isMobile ? '16px' : '24px'};
    box-shadow: 0px;
    margin: ${props => props.$isMobile ? '12px 0' : '20px 0'};
    text-align: center;
    animation: fadeInScale 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    
    &:hover {
      transform: translateY(-5px);
      transition: all 0.3s ease;
    }
  }
`;

const WelcomeTitle = styled(Typography)`
  && {
    font-weight: 700;
    font-size: ${props => props.$isMobile ? '1.4rem' : '1.8rem'};
    margin-bottom: ${props => props.$isMobile ? '12px' : '16px'};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
`;

const WelcomeText = styled(Typography)`
  && {
    font-size: ${props => props.$isMobile ? '0.95rem' : '1.1rem'};
    line-height: 1.6;
    margin-bottom: ${props => props.$isMobile ? '12px' : '20px'};
    font-weight: 500;
  }
`;

const Emoji = styled.span`
  font-size: ${props => props.$isMobile ? '1.2rem' : '1.4rem'};
  margin-right: 8px;
`;

const ChatHistory = ({ messages, streamingMessage, isStreaming, isMobile = false }) => {

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, isStreaming]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      scrollToBottom();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getWelcomeMessage = () => {
    const now = new Date();
    const hour = now.getHours();
    let greeting = '';
    let emoji = '';

    if (hour >= 5 && hour < 9) {
      greeting = '早上好！新的一天开始了，有什么我可以帮您的吗？';
      emoji = '🌅';
    } else if (hour >= 9 && hour < 12) {
      greeting = '上午好！希望您今天过得愉快，有什么需要帮助的吗？';
      emoji = '🌤️';
    } else if (hour >= 12 && hour < 14) {
      greeting = '中午好！午餐时间到了，有什么我可以协助您的吗？';
      emoji = '☀️';
    } else if (hour >= 14 && hour < 18) {
      greeting = '下午好！工作顺利吗？有什么问题需要我帮忙的吗？';
      emoji = '🌤️';
    } else if (hour >= 18 && hour < 22) {
      greeting = '晚上好！辛苦了一天，有什么我可以为您做的吗？';
      emoji = '🌇';
    } else {
      greeting = '您好！这么晚还在工作，有什么紧急的事情需要处理吗？';
      emoji = '✨';
    }

    return { greeting, emoji };
  };

  const renderWelcomeMessage = () => {
    const { greeting, emoji } = getWelcomeMessage();
    
    return (
      <WelcomeMessageContainer elevation={0} $isMobile={isMobile}>
        <WelcomeTitle variant="h4" $isMobile={isMobile}>
          <Emoji $isMobile={isMobile}>{emoji}</Emoji>
          欢迎来到聊天超人！
        </WelcomeTitle>
        <WelcomeText variant="body1" $isMobile={isMobile}>
          {greeting}
        </WelcomeText>
        <WelcomeText variant="body1" $isMobile={isMobile}>
          快来和我聊天吧，我会用超能力帮您解答各种问题！
        </WelcomeText>
      </WelcomeMessageContainer>
    );
  };

  return (
    <ChatHistoryContainer ref={chatContainerRef} $isMobile={isMobile}>
      {(!messages.length && renderWelcomeMessage())}
      {messages.map((message, index) => {
        return (
          <div 
            key={index} 
          >
            <Message
              message={message.content}
              isUser={message.isUser}
              timestamp={message.timestamp}
              type={message.type || (message.isUser ? 'user' : 'assistant')}
              isSynced={message.isSynced}
              isMobile={isMobile}
            />
          </div>
        );
      })}
      <div ref={chatEndRef} />
    </ChatHistoryContainer>
  );
};

export default ChatHistory;
